import React, { Component } from 'react';
import _ from 'lodash';
import MatchingDetails, {
  Details,
} from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/Matching/MatchingDetails';
import RequestEventDetails from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/RequestEventDetails';
import { ButtonGroup } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import { selectRequestDetailTab } from 'store/analytics/ui/subViews/inspectionView/inspectionViewActions';
import { fetchInspectionViewRequestsThunk } from 'store/analytics/data/analyticsDataActions';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import FullPageLoader from 'components/loaders/FullPageLoader';
import { InspectionViewEvent, InspectionViewRequest, InspectionViewRequestDetails } from 'types/rest/analyticsTypes';
import OutdatedRequest from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/OutdatedRequest';
import InspectionDetailsHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionDetailsHeader';
import { withTranslation, WithTranslation } from 'react-i18next';

type AllProps = WithTranslation & StateProps & DispatchProps & AnalyticsRequestDetailProps;

interface AnalyticsRequestDetailProps {
  serviceImageFilePath: string;
}

export class AnalyticsRequestDetail extends Component<AllProps> {
  createMatchingDetails = (
    selectedRequest: InspectionViewRequest | null,
    serviceImageFilePath: string,
    requestDetails: InspectionViewRequestDetails,
  ): Details => {
    // Build session info
    let sessionData = '(none)';

    // Check if session data is available
    if (requestDetails.sessionData) {
      const sessionKey = requestDetails.sessionData.sessionKey;
      const txnModel = requestDetails.sessionData.sessionTxn;
      const convModel = requestDetails.sessionData.sessionConv;

      if (sessionKey !== null) {
        sessionData = sessionKey;
        if (requestDetails.sessionData.sessionDisposition === true) {
          sessionData += ' (new: ';
          if (_.isNil(txnModel) && !_.isNil(convModel)) {
            sessionData += '[' + convModel.id + ']';
            sessionData += ' ' + convModel.name;
          }
          sessionData += ')';
        }
        if (!_.isNil(txnModel)) {
          sessionData += ' Search started with: ';
          if (!_.isNil(convModel)) {
            sessionData += '[' + convModel.id + ']';
            sessionData += ' ' + convModel.name;
          }
          sessionData += '[' + txnModel.id + '] ';
          sessionData += txnModel.request.operation + '\n';
          Object.keys(txnModel.request.arguments).forEach(key => {
            sessionData += key + ':' + txnModel.request.arguments[key] + '\n';
          });
        }
      }
    }

    let vseRespondFrom = '-';
    if (selectedRequest) {
      vseRespondFrom = selectedRequest.operation;
      if (selectedRequest.respondingTxn) {
        const respondingTxn = selectedRequest.respondingTxn;
        vseRespondFrom = '[' + respondingTxn.id + '] ' + selectedRequest.operation + '\n';
        if (_.get(selectedRequest, 'respondingTxn.request.arguments') !== undefined) {
          Object.keys(selectedRequest.respondingTxn.request.arguments).forEach((key: any) => {
            vseRespondFrom += key + ':' + selectedRequest.respondingTxn.request.arguments[key] + '\n';
          });
        }
      }
    }

    return {
      serviceImage: serviceImageFilePath,
      session: sessionData,
      matchType: selectedRequest ? selectedRequest.matchType : '-',
      vseRespondFrom: vseRespondFrom,
      transactionId: selectedRequest ? selectedRequest.transactionId : undefined,
    };
  };

  getMatchingSteps = (requestDetails: InspectionViewRequestDetails): InspectionViewEvent[] => {
    return !_.isNil(requestDetails.matchingData) ? requestDetails.matchingData : [];
  };

  getStepDetails = (requestDetails: InspectionViewRequestDetails) => {
    if (_.isNil(requestDetails.stepDetails)) {
      return [];
    }

    // We need to generate IDs for each step detail, it is needed because of the navpills
    return requestDetails.stepDetails.map(item => {
      return {
        ...item,
        id: _.uniqueId('step_'),
      };
    });
  };

  render() {
    //Do not continue with render if ui / data contexts are not ready
    if (_.isNil(this.props.activeUiContext) || _.isNil(this.props.activeDataContext)) {
      return <></>;
    }

    //UI State
    const uiContext = this.props.activeUiContext;
    const selectedTab = uiContext ? uiContext.inspectionView.selectedViewTab : 'defaultTab';
    const selectedRequest = uiContext ? (uiContext.inspectionView.selectedRequest as InspectionViewRequest) : null;

    //Data state
    const tabId = this.props.activeDataContext.tabId;
    const inspectionState = this.props.activeDataContext.inspection;
    const requestDetails = inspectionState ? inspectionState.requestDetails : null;
    const isLoading = inspectionState ? inspectionState.isLoadingRequestDetails : false;
    const isRequestOutdated = inspectionState ? inspectionState.isRequestOutdated : false;

    return (
      <div className="mt-5 pt-4 position-relative" style={{ borderTop: '1px solid #c8d1e0', minHeight: '400px' }}>
        {isLoading && (
          <div className="blur-background">
            <FullPageLoader />
          </div>
        )}

        {/* Request detail contents */}
        {
          // Request Header
          <div className={isLoading ? 'blur' : ''}>
            <Row className="mt-3 mb-3">
              <Col>
                <InspectionDetailsHeader
                  operation={selectedRequest && selectedRequest.operation ? selectedRequest.operation : ''}
                  seconds={true}
                  requests={selectedRequest}
                />
              </Col>

              {/* Do not show Tabs if request detail is outdated */}
              {isRequestOutdated === false && (
                <Col className={'text-right'}>
                  <ButtonGroup>
                    <Button
                      variant={selectedTab === 'defaultTab' ? 'primary' : 'secondary'}
                      onClick={() => this.props.selectRequestDetailTab('defaultTab')}
                    >
                      {this.props.t('analytics:inspection.matching')}
                    </Button>
                    <Button
                      variant={selectedTab === 'secondTab' ? 'primary' : 'secondary'}
                      onClick={() => this.props.selectRequestDetailTab('secondTab')}
                    >
                      {this.props.t('analytics:inspection.requestEventDetails')}
                    </Button>
                  </ButtonGroup>
                </Col>
              )}
            </Row>

            {/* Outdated Request Placeholder */}
            {isRequestOutdated === true && (
              <OutdatedRequest refreshRequestsHandler={() => this.props.fetchInspectionViewRequestsThunk(tabId)} />
            )}

            {/* Tab contents with request detail */}
            {requestDetails !== null && isRequestOutdated === false && (
              <>
                {selectedTab === 'defaultTab' && (
                  <MatchingDetails
                    executionSteps={this.getMatchingSteps(requestDetails)}
                    details={this.createMatchingDetails(
                      selectedRequest,
                      this.props.serviceImageFilePath,
                      requestDetails,
                    )}
                  />
                )}
                {selectedTab === 'secondTab' && (
                  <RequestEventDetails executionSteps={this.getStepDetails(requestDetails)} />
                )}
              </>
            )}
            {/* ./Tab contents */}
          </div>
        }
        {/* ./Request detail */}
      </div>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => ({
  activeUiContext: analytics.ui.activeUiContext,
  activeDataContext: analytics.data.activeDataContext,
});

const mapDispatchToProps = {
  selectRequestDetailTab,
  fetchInspectionViewRequestsThunk,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AnalyticsRequestDetail));
